﻿import { Button } from '@progress/kendo-react-buttons';
import { NumericTextBox, Slider, SliderLabel } from '@progress/kendo-react-inputs';
import { Map as MaplibreMap, NavigationControl } from 'maplibre-gl';
import React, { useEffect } from 'react';
import CustomPopover from '../../app/components/layout/CustomPopover';
import useMap from '../../app/hooks/use-map';
import { ChevronDownOutline, RestartOutline } from '../../icons/icons';

class FeNavigationControl extends NavigationControl {
    #control: HTMLElement | null = null;

    onAdd(map: MaplibreMap): HTMLElement {
        this.#control = super.onAdd(map);

        const compassEl = this.#control.querySelector('.maplibregl-ctrl-compass');
        compassEl?.classList.add(
            'k-button',
            'k-button-md',
            'k-button-solid',
            'k-button-solid-base',
            'k-rounded-md',
            'k-icon-button',
            'navigation-ctrl'
        );

        return document.createElement('div'); // map inserts empty div
    }

    getElement(): HTMLElement | null {
        return this.#control;
    }

    onRemove() {
        super.onRemove();
        this.#control = null;
    }
}

export function PitchRollControl() {
    const [controlEl, setControlEl] = React.useState<HTMLElement | null>(null);
    const containerRef = React.useRef<HTMLDivElement>(null);
    const map = useMap();
    const [bearing, setBearing] = React.useState(map?.getBearing() ?? 0);
    const [pitch, setPitch] = React.useState(map?.getPitch() ?? 0);

    useEffect(() => {
        if (map) {
            const control = new FeNavigationControl({ showCompass: true, showZoom: false, visualizePitch: true });
            map.addControl(control, 'bottom-left');
            setControlEl(control.getElement());

            const onPitch = () => {
                setPitch(map.getPitch());
            };
            const onBearing = () => {
                setBearing(map.getBearing());
            };

            map.on('pitch', onPitch);
            map.on('rotate', onBearing);

            return () => {
                setControlEl(null);
                map.off('pitch', onPitch);
                map.off('rotate', onBearing);
            };
        }
    }, [map]);

    useEffect(() => {
        if (map) {
            setBearing(map.getBearing());
            setPitch(map.getPitch());
        }
    }, [map, map?.getBearing(), map?.getPitch()]);

    useEffect(() => {
        if (controlEl && containerRef.current) {
            containerRef.current.appendChild(controlEl);
            return () => {
                if (containerRef.current?.contains(controlEl)) {
                    containerRef.current.removeChild(controlEl);
                }
            };
        }
    }, [controlEl]);

    return (
        <div>
            <div className={'ctrl-background k-card d-flex flex-row gap-1 align-items-center pe-auto overflow-visible'} style={{ width: 'fit-content' }}>
                <div ref={containerRef} className={'navigation-ctrl-container'} />

                <CustomPopover
                    trigger={
                        <Button
                            svgIcon={ChevronDownOutline}
                            style={{ width: '15px', border: 'none', paddingRight: '10px' }}></Button>
                    }
                    closeOnClick={false}
                    offsetOptions={{ crossAxis: 10, mainAxis: 5 }}>
                    {map && (
                        <div className={'m-2 d-flex flex-column gap-4'} style={{ width: 250 }}>
                            <div>
                                <div className={'d-flex justify-content-between'}>
                                    <div>Bearing</div>
                                    <NumericTextBox
                                        style={{ width: '60px' }}
                                        value={bearing}
                                        onChange={(e) => {
                                            const value = e.value ?? 0;
                                            setBearing(value);
                                            map?.setBearing(value);
                                        }}
                                        format="##0°"
                                        spinners={false}
                                        min={-180}
                                        max={180}
                                    />
                                </div>
                                <Slider
                                    min={-180}
                                    max={180}
                                    className="w-100"
                                    value={bearing}
                                    onChange={(e) => {
                                        map?.setBearing(e.value);
                                    }}>
                                    {[-180, 0, 180].map((label, i) =>
                                        label % 5 === 0 && label % 2 === 0 ? (
                                            <SliderLabel
                                                key={i}
                                                position={label}
                                                onClick={() => {
                                                    map?.setBearing(label);
                                                }}>
                                                {label.toString()}
                                            </SliderLabel>
                                        ) : (
                                            <SliderLabel key={i} position={label}></SliderLabel>
                                        )
                                    )}
                                </Slider>
                            </div>
                            <div>
                                <div className={'d-flex justify-content-between'}>
                                    <div>Pitch</div>
                                    <NumericTextBox
                                        style={{ width: '60px' }}
                                        value={pitch}
                                        onChange={(e) => {
                                            const value = e.value ?? 0;
                                            setPitch(value);
                                            map?.setPitch(value);
                                        }}
                                        format="##0°"
                                        spinners={false}
                                        min={map.getMinPitch()}
                                        max={map.getMaxPitch()}
                                    />
                                </div>
                                <Slider
                                    min={map.getMinPitch()}
                                    max={map.getMaxPitch()}
                                    className="w-100"
                                    value={pitch}
                                    onChange={(e) => {
                                        map?.setPitch(e.value);
                                    }}>
                                    {[0, map.getMaxPitch()].map((label, i) =>
                                        label % 5 === 0 && label % 2 === 0 ? (
                                            <SliderLabel
                                                key={i}
                                                position={label}
                                                onClick={() => {
                                                    map?.setPitch(label);
                                                }}>
                                                {label.toString()}
                                            </SliderLabel>
                                        ) : (
                                            <SliderLabel key={i} position={label}></SliderLabel>
                                        )
                                    )}
                                </Slider>
                            </div>
                            <Button
                                svgIcon={RestartOutline}
                                onClick={() => {
                                    map?.resetNorthPitch();
                                }}>
                                Reset
                            </Button>
                        </div>
                    )}
                </CustomPopover>
            </div>
        </div>
    );
}
